<script setup lang="ts">
import { ref, onMounted, watch, computed, type Component } from 'vue'
import CardComponent from '@/components/cards/CardComponent.vue'
import api from '@/services/api'
import LoaderDefault from '@/components/common/LoaderDefault.vue'
import { useAuthStore } from '@/stores/auth'

import {
  AdminIcon,
  AgentIcon,
  EngineerIcon,
  SettingIcon
} from '@/icons'

const props = defineProps<{
  tenderId: string
}>()

const authStore = useAuthStore()

// Audit Trail
interface Audit {
  id: string
  action: string
  by: string
  date: string
  type: 'create' | 'assign' | 'upload' | 'status' | 'note' | 'document' | 'workflow' | 'review'
  details?: string
  userType?: 'admin' | 'agent' | 'engineer' | 'system'
  priority: number
}

const allAuditTrail = ref<Audit[]>([])
const loading = ref(true)
const error = ref<string | null>(null)

// Filter audit trail based on user type
const filteredAuditTrail = computed(() => {
  const userType = authStore.user?.userType

  // First filter out watchlist-related actions
  const withoutWatchlist = allAuditTrail.value.filter(audit => {
    // Filter out any watchlist-related actions
    const watchlistActions = [
      'Added to Watchlist',
      'Removed from Watchlist',
      'Watchlist Updated',
      'Watchlist Status Changed'
    ]

    return !watchlistActions.includes(audit.action)
  })

  if (userType === 'engineer') {
    // Engineers can only see engineer-specific entries
    return withoutWatchlist.filter(audit =>
      audit.action === 'Engineer Assigned' ||
      (audit.action === 'Workflow Status Updated' && audit.userType === 'engineer') ||
      audit.userType === 'engineer'
    )
  }

  // Admin and Agent can see everything except watchlist
  return withoutWatchlist
})

// Function to get icon component based on user type
const getUserTypeIcon = (userType?: string): Component => {
  const iconMap: Record<string, Component> = {
    'admin': AdminIcon,
    'agent': AgentIcon,
    'engineer': EngineerIcon,
    'system': SettingIcon,
  }

  return iconMap[userType || ''] || SettingIcon // Default to SettingIcon if not found
}

// Function to get icon color class based on user type
const getIconColorClass = (userType?: string): string => {
  const classMap: Record<string, string> = {
    'admin': 'text-blue-600 dark:text-blue-400',
    'agent': 'text-green-600 dark:text-green-400',
    'engineer': 'text-purple-600 dark:text-purple-400',
    'system': 'text-gray-600 dark:text-gray-400'
  }
  return classMap[userType || ''] || 'text-gray-600 dark:text-gray-400'
}

// Priority mapping for custom ordering
const getPriority = (action: string): number => {
  const priorityMap: Record<string, number> = {
    // Highest priority (latest events)
    'Workflow Status Updated': 100,
    'Engineer Notes': 95,
    'Engineer Assigned': 90,
    'Tender Lost': 85,
    'Tender Won': 80,
    'Under Query': 78,
    'Tender Submitted': 75,
    'Not Fit': 70,
    'Not Bidded': 68,
    'Admin Review': 65,
    'Criteria Review': 60,
    'Price Review': 55,
    'Agent Notes': 50,
    'Assignment Notes': 45,
    'Agent Assigned': 40,
    'Tender Created': 30,
    'Tender Uploaded/Updated': 20,
  }
  return priorityMap[action] || 10
}

// SIMPLE function to fetch user name by ID
const fetchUserName = async (userId: string): Promise<string> => {
  if (!userId) return 'System'

  // Handle special cases
  if (userId.toLowerCase() === 'admin') return 'Administrator'
  if (userId.toLowerCase() === 'system') return 'System'
  if (userId.toLowerCase() === 'agent') return 'Agent'
  if (userId.toLowerCase() === 'engineer') return 'Engineer'

  try {
    const response = await api.get(`/api/users/${userId}`)
    if (response.data && response.data.user) {
      return response.data.user.displayName || response.data.user.name || 'User'
    }
  } catch (err) {
    console.warn(`Failed to fetch user name for ID: ${userId}`, err)
  }

  return 'User'
}

// Function to fetch user type by ID
const fetchUserType = async (userId: string): Promise<'admin' | 'agent' | 'engineer' | 'system' | null> => {
  if (!userId) return 'system'

  // Handle special cases
  if (userId.toLowerCase() === 'admin') return 'admin'
  if (userId.toLowerCase() === 'system') return 'system'
  if (userId.toLowerCase() === 'agent') return 'agent'
  if (userId.toLowerCase() === 'engineer') return 'engineer'

  try {
    const response = await api.get(`/api/users/${userId}`)
    if (response.data && response.data.user && response.data.user.userType) {
      return response.data.user.userType
    }
  } catch (err) {
    console.warn(`Failed to fetch user type for ID: ${userId}`, err)
  }

  return null
}


// SIMPLE determineUserType function - Fixed for agent actions
const determineUserType = async (action: string, userId?: string): Promise<'admin' | 'agent' | 'engineer' | 'system'> => {
  // System actions
  if (action === 'Tender Uploaded/Updated' || action === 'Tender Created' || action === 'Not Bidded') {
    return 'system'
  }

  // Always use actual user type from database
  if (userId) {
    const userType = await fetchUserType(userId)
    if (userType) {
      return userType
    }
  }

  // Admin-only actions
  if (['Agent Assigned', 'Assignment Notes', 'Admin Review', 'Engineer Assigned', 'Not Fit', 'Engineer Notes'].includes(action)) {
    return 'admin'
  }

  // Agent-only actions - Added 'Under Query' here
  if (['Agent Notes', 'Price Review', 'Criteria Review', 'Tender Submitted', 'Under Query', 'Tender Won', 'Tender Lost', 'In Progress'].includes(action)) {
    return 'agent'
  }

  // Engineer-only actions
  if (action === 'Workflow Status Updated') {
    // Check if done by engineer
    if (userId) {
      const userType = await fetchUserType(userId)
      if (userType === 'engineer') {
        return 'engineer'
      }
    }
    return 'admin' // fallback
  }

  // Default to admin
  return 'admin'
}

// Helper function to add audit item
const addAuditItem = async (
  id: string,
  action: string,
  userId: string,  // This could be 'admin', 'system', or actual user ID
  date: string,
  type: Audit['type'],
  details?: string
): Promise<void> => {
  if (!date) return

  const by = await fetchUserName(userId)
  const userType = await determineUserType(action, userId)

  allAuditTrail.value.push({
    id,
    action,
    by,
    date,
    type,
    details,
    userType,
    priority: getPriority(action)
  })
}

// SIMPLIFIED: Fetch all audit data from tender record
const fetchAuditTrail = async () => {
  try {
    loading.value = true
    error.value = null
    console.log(`📥 Fetching audit trail for tender: ${props.tenderId}`)

    // Fetch tender details
    const response = await api.get(`/api/tenders/id/${props.tenderId}`)

    if (response.data) {
      const tenderData = response.data
      allAuditTrail.value = []

      // 1. Tender Uploaded/Updated (By Admin/System)
      if (tenderData.uploadedAt || tenderData.updatedAt) {
        await addAuditItem(
          'upload-update',
          'Tender Uploaded/Updated',
          tenderData.uploadedBy || 'system',
          tenderData.uploadedAt || tenderData.createdAt || tenderData.updatedAt,
          'upload',
          tenderData.sourceFile ? `Uploaded from: ${tenderData.sourceFile}` : 'Manual update'
        )
      }

      // 2. Tender Created (By Admin/System)
      if (tenderData.createdAt) {
        await addAuditItem(
          'creation',
          'Tender Created',
          tenderData.uploadedBy || 'system',
          tenderData.createdAt,
          'create',
          `Tender ${tenderData.t247Id || tenderData.referenceNo || 'ID'} created in system`
        )
      }

      // 3. Agent Assigned (By Admin)
      if (tenderData.assignedToAgent) {
        const assignedBy = tenderData.reviewedBy || tenderData.uploadedBy || 'admin'
        const agentName = await fetchUserName(tenderData.assignedToAgent)

        await addAuditItem(
          'agent-assignment',
          'Agent Assigned',
          assignedBy,
          tenderData.agentAssignedAt || tenderData.updatedAt || tenderData.createdAt,
          'assign',
          `Assigned to ${agentName} for execution`
        )
      }

      // 4. Assignment Notes (Admin note for Agent)
      if (tenderData.assignmentNotes) {
        const adminId = tenderData.reviewedBy || tenderData.uploadedBy || 'admin'

        await addAuditItem(
          'assignment-notes',
          'Assignment Notes',
          adminId,
          tenderData.updatedAt || tenderData.createdAt,
          'note',
          tenderData.assignmentNotes
        )
      }

      // 5. In Progress (By Agent)
      // if (tenderData.workflowStatus === 'in-progress') {
      //   await addAuditItem(
      //     'in-progress',
      //     'In Progress',
      //     tenderData.assignedToAgent,
      //     tenderData.updatedAt || tenderData.agentAssignedAt,
      //     'status',
      //     'Tender work is in progress'
      //   )
      // }

      // 6. Agent Notes (By Agent)
      if (tenderData.agentNotes) {
        await addAuditItem(
          'agent-notes',
          'Agent Notes',
          tenderData.assignedToAgent,
          tenderData.updatedAt || tenderData.agentAssignedAt,
          'note',
          tenderData.agentNotes
        )
      }

      // 7. Price Review (By Agent)
      if (tenderData.priceReviewNotes) {
        await addAuditItem(
          'price-review',
          'Price Review',
          tenderData.assignedToAgent,
          tenderData.priceReviewedAt || tenderData.updatedAt,
          'review',
          tenderData.priceReviewNotes
        )
      }

      // 8. Criteria Review (By Agent)
      if (tenderData.criteriaReviewNotes) {
        await addAuditItem(
          'criteria-review',
          'Criteria Review',
          tenderData.assignedToAgent,
          tenderData.criteriaReviewedAt || tenderData.updatedAt,
          'review',
          tenderData.criteriaReviewNotes
        )
      }

      // 9. Admin Review (Ready-to-bid - By Admin)
      if (tenderData.adminReviewNotes && tenderData.workflowStatus !== 'not-fit') {
        const adminId = tenderData.reviewedBy || 'admin'

        await addAuditItem(
          'admin-review',
          'Admin Review',
          adminId,
          tenderData.reviewedAt || tenderData.updatedAt,
          'review',
          tenderData.adminReviewNotes
        )
      }

      // 10. Not Fit (By Admin)
      if (tenderData.workflowStatus === 'not-fit' || tenderData.status === 'rejected') {
        const notes = tenderData.rejectionComment || tenderData.adminReviewNotes || 'Marked as not fit'
        const adminId = tenderData.rejectedBy || tenderData.reviewedBy || 'admin'

        await addAuditItem(
          'not-fit',
          'Not Fit',
          adminId,
          tenderData.rejectedAt || tenderData.updatedAt,
          'status',
          notes
        )
      }

      // 10.5 Not Bidded (Deadline expired before agent submitted - By System)
      if (tenderData.workflowStatus === 'not-bidded') {
        await addAuditItem(
          'not-bidded',
          'Not Bidded',
          'system',
          tenderData.expiredAt || tenderData.updatedAt,
          'status',
          'Deadline passed before the assigned agent submitted a bid'
        )
      }

      // 11. Tender Submitted (By Agent)
      if (tenderData.agentSubmissionDate || tenderData.workflowStatus === 'submitted') {
        const submitterId = tenderData.agentSubmittedBy || tenderData.submittedBy || tenderData.assignedToAgent

        if (submitterId) {
          await addAuditItem(
            'tender-submission',
            'Tender Submitted',
            submitterId,
            tenderData.agentSubmissionDate || tenderData.submittedAt || tenderData.updatedAt,
            'status',
            tenderData.agentSubmissionNotes || tenderData.submissionNotes || 'Tender bid has been submitted'
          )
        }
      }

      // 12. Under Query (By Agent)
      if (tenderData.underQueryNotes || tenderData.workflowStatus === 'under-query') {
        const agentId = tenderData.assignedToAgent || 'agent'

        // Check if we have specific under query review info
        const underQueryBy = tenderData.underQueryReviewedBy || agentId
        const underQueryAt = tenderData.underQueryReviewedAt || tenderData.updatedAt

        await addAuditItem(
          'under-query',
          'Under Query',
          underQueryBy,
          underQueryAt,
          'status',
          tenderData.underQueryNotes || 'Query raised by buyer - needs response'
        )
      }


      // 13. Tender Won (By Agent)
      if (tenderData.wonAt || tenderData.workflowStatus === 'won') {
        const agentId = tenderData.assignedToAgent || 'agent'

        const details = tenderData.wonNotes ||
                       (tenderData.contractValue ? `Contract value: ₹${Number(tenderData.contractValue).toLocaleString('en-IN')}` : 'Tender won successfully')

        await addAuditItem(
          'won-status',
          'Tender Won',
          agentId,
          tenderData.wonAt || tenderData.updatedAt,
          'status',
          details
        )
      }

      // 14. Tender Lost (By Agent)
      if (tenderData.lostAt || tenderData.workflowStatus === 'lost') {
        const agentId = tenderData.assignedToAgent || 'agent'

        await addAuditItem(
          'lost-status',
          'Tender Lost',
          agentId,
          tenderData.lostAt || tenderData.updatedAt,
          'status',
          tenderData.lostNotes || 'Tender was not successful'
        )
      }

      // 15. Engineer Assigned (By Admin)
      if (tenderData.assignedToEngineer) {
        const adminId = tenderData.reviewedBy || tenderData.uploadedBy || 'admin'
        const engineerName = await fetchUserName(tenderData.assignedToEngineer)

        await addAuditItem(
          'engineer-assignment',
          'Engineer Assigned',
          adminId,
          tenderData.engineerAssignedAt || tenderData.updatedAt,
          'assign',
          `Assigned to ${engineerName} for project execution`
        )
      }

      // 16. Engineer Notes (By Admin) - FIXED: This is admin, not engineer
      if (tenderData.engineerNotes) {
        const adminId = tenderData.reviewedBy || tenderData.uploadedBy || 'admin'

        await addAuditItem(
          'engineer-notes',
          'Engineer Notes',
          adminId,
          tenderData.updatedAt,
          'note',
          tenderData.engineerNotes
        )
      }

      // 17. Workflow Status Updated (Completed - By Engineer)
      if (tenderData.workflowStatus === 'completed') {
        const engineerId = tenderData.assignedToEngineer

        if (engineerId) {
          const by = await fetchUserName(engineerId)

          allAuditTrail.value.push({
            id: 'workflow-completed',
            action: 'Workflow Status Updated',
            by,
            date: tenderData.engineerCompletionDate || tenderData.updatedAt,
            type: 'workflow',
            details: tenderData.tenderCompletedNotes || 'Tender workflow marked as completed',
            userType: 'engineer',
            priority: getPriority('Workflow Status Updated')
          })
        }
      }

      // Sort by priority (highest priority = latest events at top)
      allAuditTrail.value.sort((a, b) => {
        if (b.priority !== a.priority) {
          return b.priority - a.priority
        }
        return new Date(b.date).getTime() - new Date(a.date).getTime()
      })

      console.log(`✅ Found ${allAuditTrail.value.length} audit records`)

      // Debug log
      console.log('Audit Trail Summary:')
      allAuditTrail.value.forEach(audit => {
        console.log(`- ${audit.action} | By: ${audit.by} | Type: ${audit.userType}`)
      })

    } else {
      error.value = 'No tender data found'
    }
  } catch (err) {
    console.error('❌ Failed to fetch audit trail:', err)
    error.value = 'Failed to load audit trail. Please try again.'
  } finally {
    loading.value = false
  }
}

// Get user badge color
const getUserBadgeClass = (userType?: string): string => {
  const classMap: Record<string, string> = {
    'admin': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
    'agent': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    'engineer': 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
    'system': 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300'
  }
  return classMap[userType || ''] || 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300'
}

// Get audit border class based on user type
const getAuditBorderClass = (userType?: string): string => {
  const classMap: Record<string, string> = {
    'admin': 'border-l-blue-500 bg-blue-50 dark:bg-blue-900/10',
    'agent': 'border-l-green-500 bg-green-50 dark:bg-green-900/10',
    'engineer': 'border-l-purple-500 bg-purple-50 dark:bg-purple-900/10',
    'system': 'border-l-gray-500 bg-gray-50 dark:bg-gray-900/10'
  }
  return classMap[userType || ''] || 'border-l-gray-500 bg-gray-50 dark:bg-gray-900/10'
}

// Get icon background color based on user type
const getAuditIconBgClass = (userType?: string): string => {
  const classMap: Record<string, string> = {
    'admin': 'bg-blue-100 dark:bg-blue-900/30',
    'agent': 'bg-green-100 dark:bg-green-900/30',
    'engineer': 'bg-purple-100 dark:bg-purple-900/30',
    'system': 'bg-gray-100 dark:bg-gray-900/30'
  }
  return classMap[userType || ''] || 'bg-gray-100 dark:bg-gray-900/30'
}

const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  } catch {
    return dateString
  }
}

// Watch for tenderId changes
watch(() => props.tenderId, () => {
  if (props.tenderId) {
    fetchAuditTrail()
  }
})

onMounted(() => {
  if (props.tenderId) {
    fetchAuditTrail()
  }
})
</script>

<template>
  <CardComponent
    title="Audit Trail"
    :showHeaderBorder="true"
    :showHeaderPaddingBottom="true"
  >
    <template #body>
      <!-- Loading State -->
      <LoaderDefault v-if="loading" title="Loading audit trail..." />

      <!-- Error State -->
      <div v-else-if="error" class="text-center py-8">
        <p class="text-gray-500 dark:text-gray-400">{{ error }}</p>
        <button
          @click="fetchAuditTrail"
          class="mt-2 px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors"
        >
          Retry
        </button>
      </div>

      <!-- Audit Trail Content -->
      <div v-else class="flex flex-col flex-1 space-y-4">
        <!-- Show filtered audit trail based on user type -->
        <div
          v-for="audit in filteredAuditTrail"
          :key="audit.id"
          class="flex flex-1 max-w-full gap-3 p-3 border-l-4 rounded-r-md"
          :class="getAuditBorderClass(audit.userType)"
        >
          <div
            :class="getAuditIconBgClass(audit.userType)"
            class="w-8 h-8 rounded-full flex items-center justify-center"
          >
            <!-- Dynamic icon based on user type -->
            <component
              :is="getUserTypeIcon(audit.userType)"
              class="w-4 h-4"
              :class="getIconColorClass(audit.userType)"
            />
          </div>
          <div class="flex-1 min-w-0">
            <div class="flex items-center justify-between gap-2 mb-1">
              <p class="text-sm font-medium text-gray-800 dark:text-white truncate">
                {{ audit.action }}
              </p>
              <span v-if="audit.userType" class="text-xs px-2 py-0.5 rounded-full shrink-0" :class="getUserBadgeClass(audit.userType)">
                {{ audit.userType.toUpperCase() }}
              </span>
            </div>

            <div class="grid grid-cols-2 items-center gap-2 mb-2">
              <span class="text-xs text-gray-500 dark:text-gray-400 truncate">
                By {{ audit.by }}
              </span>
              <span class="text-xs text-gray-500 dark:text-gray-400 shrink-0">
                {{ formatDate(audit.date) }}
              </span>
            </div>

            <p v-if="audit.details" class="text-xs text-gray-600 dark:text-gray-400 mt-2 whitespace-pre-wrap bg-black/5 dark:bg-white/5 p-2 rounded">
              {{ audit.details }}
            </p>
          </div>
        </div>

        <!-- Empty State for filtered view -->
        <div v-if="filteredAuditTrail.length === 0" class="text-center py-8">
          <div class="w-12 h-12 mx-auto mb-4 text-gray-400">
            <svg class="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <p class="text-gray-500 dark:text-gray-400 mb-2">No audit records found</p>
          <p class="text-sm text-gray-400 dark:text-gray-500">
            {{ authStore.user?.userType === 'engineer' ? 'Engineer audit history will appear here' : 'Activity history will appear here' }}
          </p>
        </div>
      </div>
    </template>
  </CardComponent>
</template>
